// pages/ServiceDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Shield,
  CheckCircle,
  Plus,
  Minus,
  ShoppingCart,
  Tag,
  Star,
  Users,
  Award,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Calendar,
  MapPin,
  Phone,
  MessageCircle,
  ShieldCheck,
  Home,
  Settings,
  Wind,
  Droplets,
  Zap,
  Tv,
  Refrigerator,
  Microwave,
  Battery,
  Fan,
  Thermometer,
  Drill,
  Scissors,
  Bath,
  Heart,
  Share2,
  X,
  AlertCircle
} from 'lucide-react';
import { getServiceById, getServices } from '../services/api'; // Removed getSimilarServices
import { useCart } from '../context/CartContext';

const ServiceDetail = ({ user }) => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedBHK, setSelectedBHK] = useState('1BHK');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [similarServices, setSimilarServices] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Color scheme
  const primaryColor = '#096651';
  const primaryLight = '#0d8a72';
  const primaryDark = '#064a3d';
  const accentColor = '#ff7e5f';
  const lightBg = '#f8fffd';
  const textDark = '#1a2b23';

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);
        setError('');

        // Check if we have service data in location state first
        if (location.state?.service) {
          setService(location.state.service);
          // Fetch additional details if needed
          try {
            const serviceData = await getServiceById(
              location.state.service.id || serviceId, 
              selectedBHK
            );
            setService(prev => ({ ...prev, ...serviceData }));
          } catch (apiError) {
            console.log('Using cached service data');
          }
        } else {
          // Fetch from API
          const serviceData = await getServiceById(serviceId, selectedBHK);
          setService(serviceData);
        }

        // Fetch similar services using getServices and filter by category
        if (service) {
          const allServices = await getServices();
          const similar = allServices
            .filter(s => s.category === service?.category && s.id !== service?.id)
            .slice(0, 4);
          setSimilarServices(similar);
        }

        // Set default variant if available
        if (location.state?.service?.variants?.[0]) {
          setSelectedVariant(location.state.service.variants[0]);
        }

      } catch (error) {
        console.error('Error fetching service details:', error);
        setError('Service not found. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetails();
  }, [serviceId, location.state, selectedBHK, service?.category]); // Added service?.category to dependency array

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      setSelectedQuantity(newQuantity);
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!service) return;
    
    setAddingToCart(true);
    
    const serviceToAdd = {
      ...service,
      price: getCurrentPrice(),
      selectedQuantity,
      selectedBHK: service.pricingType === 'bhk-based' ? selectedBHK : undefined,
      selectedVariant: service.pricingType === 'variants' ? selectedVariant : undefined
    };
    
    addToCart(serviceToAdd, selectedQuantity);
    
    setTimeout(() => {
      setAddingToCart(false);
    }, 1000);
  };

  const handleBookNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/', { 
        state: { 
          message: 'Please login to book this service',
          showAuth: true 
        } 
      });
      return;
    }
    
    if (!service) return;
    
    const serviceToBook = {
      ...service,
      quantity: selectedQuantity,
      price: getCurrentPrice(),
      selectedBHK: service.pricingType === 'bhk-based' ? selectedBHK : undefined,
      selectedVariant: service.pricingType === 'variants' ? selectedVariant : undefined
    };
    
    navigate('/booking', { 
      state: { 
        service: serviceToBook
      } 
    });
  };

  const getCurrentPrice = () => {
    if (!service) return 0;

    if (service.pricingType === 'inspection') {
      return service.visitingCharge || service.basePrice || 0;
    }

    if (service.pricingType === 'bhk-based' && service.bhkPrices) {
      return service.bhkPrices[selectedBHK] || service.basePrice || 0;
    } else if (service.pricingType === 'variants' && selectedVariant) {
      return selectedVariant.price || service.basePrice || 0;
    } else if (service.pricingType === 'per-unit' && service.priceMultiplier) {
      return service.priceMultiplier * selectedQuantity;
    } else {
      return service.calculatedPrice || service.basePrice || 0;
    }
  };

  const getServiceImages = () => {
    if (!service) return [];
    
    const imageData = service.images || service.image;
    
    if (!imageData) return [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=800&auto=format&fit=crop'
    ];
    
    if (typeof imageData === 'string') {
      try {
        return JSON.parse(imageData);
      } catch (e) {
        return [imageData];
      }
    }
    
    if (Array.isArray(imageData)) {
      return imageData;
    }
    
    return [];
  };

  const getServiceIcon = (serviceName) => {
    const iconProps = { size: isMobile ? 48 : 64 };

    const icons = {
      'House Cleaning': <Home {...iconProps} />,
      'Kitchen Cleaning': <Home {...iconProps} />,
      'Bathroom Cleaning': <Bath {...iconProps} />,
      'Sofa Cleaning': <Home {...iconProps} />,
      'Water Tank Cleaning': <Droplets {...iconProps} />,
      'AC Service': <Wind {...iconProps} />,
      'Split AC Jet Service': <Wind {...iconProps} />,
      'Split AC Installation': <Wind {...iconProps} />,
      'Split AC Uninstall': <Wind {...iconProps} />,
      'Window AC Jet Service': <Wind {...iconProps} />,
      'Complete Kitchen Cleaning': <Home {...iconProps} />,
      'Refrigerator Repair': <Refrigerator {...iconProps} />,
      'Washing Machine Repair': <Settings {...iconProps} />,
      'Microwave Oven Repair': <Microwave {...iconProps} />,
      'LED TV Repair': <Tv {...iconProps} />,
      'TV Installation': <Tv {...iconProps} />,
      'Inverter Repair': <Battery {...iconProps} />,
      'Fan Repair': <Fan {...iconProps} />,
      'Fan Installation': <Fan {...iconProps} />,
      'Geyser Repair': <Thermometer {...iconProps} />,
      'Geyser Service/Cleaning': <Thermometer {...iconProps} />,
      'Burner Hob Service/Cleaning': <Settings {...iconProps} />,
      'Electrician Service': <Zap {...iconProps} />,
      'Plumber Service': <Droplets {...iconProps} />,
      'Carpenter Service': <Drill {...iconProps} />,
      'Men Saloon': <Scissors {...iconProps} />,
      'Women Saloon': <Scissors {...iconProps} />
    };

    return icons[serviceName] || <Settings {...iconProps} />;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN').format(Math.round(price));
  };

  const getServiceFeatures = () => {
    const defaultFeatures = [
      { icon: ShieldCheck, text: 'Verified Professionals', color: '#4CAF50' },
      { icon: Award, text: 'Quality Service Guarantee', color: '#FF9800' },
      { icon: Clock, text: 'On-Time Service', color: '#2196F3' },
      { icon: Users, text: 'Trained Experts', color: '#9C27B0' }
    ];

    return service?.features || defaultFeatures;
  };

  const getServiceIncludes = () => {
    if (service?.includes?.length > 0) {
      return service.includes;
    }

    const defaultIncludes = {
      'AC Service': ['Dust cleaning', 'Gas pressure check', 'Cooling test', 'Complete servicing'],
      'Home Cleaning': ['Floor cleaning', 'Bathroom cleaning', 'Kitchen cleaning', 'Dusting'],
      'Plumber Service': ['Leakage check', 'Pipe repair', 'Fixture installation', 'Water pressure check'],
      'Electrician Service': ['Wiring check', 'Switch repair', 'Fixture installation', 'Safety check']
    };

    return defaultIncludes[service?.category] || [
      'Professional service',
      'Quality materials',
      'Safety standards',
      'Post-service support'
    ];
  };

  // Inline Styles
  const styles = {
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      color: textDark,
      backgroundColor: '#ffffff',
      minHeight: '100vh',
    },
    header: {
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backgroundColor: 'white',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      borderBottom: `1px solid ${primaryColor}10`,
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '12px 16px' : '16px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: 'transparent',
      border: 'none',
      color: primaryColor,
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      padding: isMobile ? '8px 12px' : '10px 16px',
      borderRadius: '8px',
      transition: 'all 0.3s ease',
    },
    actionsContainer: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: isMobile ? '36px' : '40px',
      height: isMobile ? '36px' : '40px',
      borderRadius: '50%',
      border: `1px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '20px',
    },
    // Image Gallery
    gallerySection: {
      marginBottom: isMobile ? '20px' : '30px',
    },
    mainImageContainer: {
      position: 'relative',
      width: '100%',
      height: isMobile ? '250px' : '400px',
      borderRadius: isMobile ? '12px' : '16px',
      overflow: 'hidden',
      backgroundColor: lightBg,
      cursor: 'pointer',
    },
    mainImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    },
    imageNavButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: isMobile ? '40px' : '48px',
      height: isMobile ? '40px' : '48px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      color: textDark,
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease',
      zIndex: 2,
    },
    thumbnailContainer: {
      display: 'flex',
      gap: '10px',
      marginTop: '12px',
      overflowX: 'auto',
      paddingBottom: '8px',
      scrollbarWidth: 'none',
    },
    thumbnail: {
      flexShrink: 0,
      width: isMobile ? '80px' : '100px',
      height: isMobile ? '60px' : '80px',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      border: `2px solid transparent`,
      transition: 'all 0.3s ease',
      opacity: 0.7,
    },
    activeThumbnail: {
      borderColor: primaryColor,
      opacity: 1,
      transform: 'scale(1.05)',
    },
    thumbnailImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    // Service Info
    serviceInfo: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
      gap: isMobile ? '24px' : '40px',
      marginBottom: '40px',
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    serviceHeader: {
      paddingBottom: '20px',
      borderBottom: `1px solid ${primaryColor}10`,
    },
    categoryBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: `${primaryColor}10`,
      color: primaryColor,
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      marginBottom: '12px',
    },
    serviceTitle: {
      fontSize: isMobile ? 'clamp(1.5rem, 4vw, 2rem)' : 'clamp(2rem, 3vw, 2.5rem)',
      fontWeight: '700',
      lineHeight: '1.3',
      marginBottom: '12px',
      color: textDark,
    },
    serviceSubtitle: {
      fontSize: isMobile ? '14px' : '16px',
      color: '#666',
      lineHeight: '1.6',
      marginBottom: '16px',
    },
    ratingContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
    },
    ratingBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: '#FFD70010',
      color: '#B8860B',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: '600',
    },
    reviewCount: {
      fontSize: '14px',
      color: '#666',
    },
    // Features Section
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '16px',
      marginTop: '20px',
    },
    featureCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: isMobile ? '16px 8px' : '20px 12px',
      backgroundColor: lightBg,
      borderRadius: '12px',
      border: `1px solid ${primaryColor}10`,
      transition: 'all 0.3s ease',
    },
    featureIcon: {
      width: isMobile ? '40px' : '48px',
      height: isMobile ? '40px' : '48px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '12px',
      backgroundColor: `${primaryColor}08`,
    },
    featureText: {
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      color: textDark,
      lineHeight: '1.4',
    },
    // Tabs Section
    tabsContainer: {
      marginTop: '24px',
    },
    tabsHeader: {
      display: 'flex',
      borderBottom: `1px solid ${primaryColor}10`,
      marginBottom: '24px',
      overflowX: 'auto',
      scrollbarWidth: 'none',
    },
    tabButton: {
      padding: isMobile ? '12px 16px' : '16px 24px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      color: '#666',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      borderBottom: `3px solid transparent`,
      transition: 'all 0.3s ease',
    },
    activeTab: {
      color: primaryColor,
      borderBottom: `3px solid ${primaryColor}`,
    },
    tabContent: {
      animation: 'fadeIn 0.3s ease',
    },
    includesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '12px',
    },
    includeItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      backgroundColor: lightBg,
      borderRadius: '8px',
      border: `1px solid ${primaryColor}10`,
    },
    includeIcon: {
      color: primaryColor,
      flexShrink: 0,
    },
    includeText: {
      fontSize: '14px',
      color: textDark,
      fontWeight: '500',
    },
    // Booking Card
    bookingCard: {
      position: isMobile ? 'static' : 'sticky',
      top: '100px',
      backgroundColor: 'white',
      borderRadius: '16px',
      border: `1px solid ${primaryColor}10`,
      boxShadow: '0 8px 32px rgba(9, 102, 81, 0.08)',
      padding: isMobile ? '20px' : '24px',
      marginBottom: isMobile ? '20px' : '0',
    },
    priceSection: {
      marginBottom: '24px',
    },
    originalPrice: {
      fontSize: isMobile ? '14px' : '16px',
      color: '#999',
      textDecoration: 'line-through',
      marginBottom: '4px',
    },
    currentPrice: {
      fontSize: isMobile ? 'clamp(1.8rem, 4vw, 2.2rem)' : 'clamp(2rem, 3vw, 2.5rem)',
      fontWeight: '800',
      color: primaryColor,
      marginBottom: '8px',
    },
    priceNote: {
      fontSize: isMobile ? '12px' : '14px',
      color: '#666',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginBottom: '4px',
    },
    // BHK Selector
    bhkSelector: {
      marginBottom: '24px',
    },
    bhkTitle: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      marginBottom: '12px',
      color: textDark,
    },
    bhkOptions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
      gap: '10px',
    },
    bhkOption: {
      padding: '12px',
      borderRadius: '8px',
      border: `2px solid ${primaryColor}20`,
      backgroundColor: 'white',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
    },
    activeBhkOption: {
      borderColor: primaryColor,
      backgroundColor: `${primaryColor}05`,
    },
    bhkLabel: {
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      color: textDark,
    },
    bhkPrice: {
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '700',
      color: primaryColor,
    },
    // Variant Selector
    variantSelector: {
      marginBottom: '24px',
    },
    variantOption: {
      padding: '16px',
      borderRadius: '12px',
      border: `2px solid ${primaryColor}20`,
      backgroundColor: 'white',
      marginBottom: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    activeVariantOption: {
      borderColor: primaryColor,
      backgroundColor: `${primaryColor}05`,
    },
    variantInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4px',
    },
    variantName: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      color: textDark,
    },
    variantDesc: {
      fontSize: isMobile ? '12px' : '14px',
      color: '#666',
    },
    // Quantity Selector
    quantitySection: {
      marginBottom: '24px',
    },
    quantityTitle: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      marginBottom: '12px',
      color: textDark,
    },
    quantityControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      backgroundColor: lightBg,
      borderRadius: '12px',
      padding: '8px',
      maxWidth: '200px',
    },
    quantityButton: {
      width: isMobile ? '36px' : '40px',
      height: isMobile ? '36px' : '40px',
      borderRadius: '8px',
      border: `1px solid ${primaryColor}30`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: '20px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
    },
    quantityDisplay: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '700',
      color: textDark,
      minWidth: '40px',
      textAlign: 'center',
    },
    // Action Buttons
    actionButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginTop: '24px',
    },
    addToCartButton: {
      backgroundColor: 'white',
      color: primaryColor,
      border: `2px solid ${primaryColor}`,
      padding: isMobile ? '14px' : '16px',
      borderRadius: '12px',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
    },
    bookNowButton: {
      backgroundColor: primaryColor,
      color: 'white',
      border: 'none',
      padding: isMobile ? '14px' : '16px',
      borderRadius: '12px',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
    },
    // Benefits Section
    benefitsSection: {
      backgroundColor: lightBg,
      borderRadius: '16px',
      padding: isMobile ? '20px' : '30px',
      marginTop: '30px',
    },
    benefitsTitle: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '700',
      marginBottom: '20px',
      color: textDark,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    benefitsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '16px',
    },
    benefitItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    benefitIcon: {
      width: isMobile ? '40px' : '48px',
      height: isMobile ? '40px' : '48px',
      borderRadius: '50%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: primaryColor,
      flexShrink: 0,
      border: `1px solid ${primaryColor}20`,
    },
    benefitText: {
      fontSize: isMobile ? '14px' : '15px',
      color: textDark,
      fontWeight: '500',
    },
    // Similar Services
    similarServices: {
      marginTop: '60px',
    },
    sectionTitle: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: '700',
      marginBottom: '24px',
      color: textDark,
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '16px' : '24px',
    },
    serviceCard: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      border: `1px solid ${primaryColor}10`,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    serviceCardImage: {
      width: '100%',
      height: '160px',
      objectFit: 'cover',
    },
    serviceCardContent: {
      padding: '20px',
    },
    serviceCardTitle: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '8px',
      color: textDark,
    },
    serviceCardPrice: {
      fontSize: '18px',
      fontWeight: '700',
      color: primaryColor,
      marginBottom: '12px',
    },
    // Loading
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: '20px',
    },
    loadingSpinner: {
      border: `4px solid ${lightBg}`,
      borderTop: `4px solid ${primaryColor}`,
      borderRadius: '50%',
      width: '60px',
      height: '60px',
      animation: 'spin 1s linear infinite',
    },
    // Error
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: '40px',
      textAlign: 'center',
    },
    errorTitle: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '16px',
      color: textDark,
    },
    errorText: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '30px',
      maxWidth: '400px',
    },
    // Image Modal
    imageModalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    imageModal: {
      position: 'relative',
      maxWidth: '90%',
      maxHeight: '90%',
      width: 'auto',
      height: 'auto',
    },
    modalImage: {
      maxWidth: '100%',
      maxHeight: '80vh',
      objectFit: 'contain',
      borderRadius: '8px',
    },
    modalClose: {
      position: 'absolute',
      top: '-40px',
      right: '0',
      backgroundColor: 'transparent',
      border: 'none',
      color: 'white',
      fontSize: '24px',
      cursor: 'pointer',
      padding: '8px',
    },
    modalNav: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      fontSize: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    },
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <button 
              style={styles.backButton}
              onClick={() => navigate(-1)}
              onMouseEnter={(e) => e.target.style.backgroundColor = `${primaryColor}10`}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <ArrowLeft size={20} />
              Back
            </button>
          </div>
        </div>
        
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: textDark }}>Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <button 
              style={styles.backButton}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={20} />
              Back
            </button>
          </div>
        </div>
        
        <div style={styles.errorContainer}>
          <AlertCircle size={48} color={accentColor} />
          <h2 style={styles.errorTitle}>Service Not Found</h2>
          <p style={styles.errorText}>
            The requested service could not be found. It may have been removed or is temporarily unavailable.
          </p>
          <button 
            style={styles.bookNowButton}
            onClick={() => navigate('/services')}
          >
            Browse All Services
          </button>
        </div>
      </div>
    );
  }

  const serviceImages = getServiceImages();
  const serviceFeatures = getServiceFeatures();
  const serviceIncludes = getServiceIncludes();
  const currentPrice = getCurrentPrice();

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <button 
            style={styles.backButton}
            onClick={() => navigate(-1)}
            onMouseEnter={(e) => e.target.style.backgroundColor = `${primaryColor}10`}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          
          <div style={styles.actionsContainer}>
            <button 
              style={styles.actionButton}
              onClick={() => {}}
              onMouseEnter={(e) => e.target.style.backgroundColor = `${primaryColor}10`}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Share2 size={18} />
            </button>
            <button 
              style={styles.actionButton}
              onClick={() => {}}
              onMouseEnter={(e) => e.target.style.backgroundColor = `${primaryColor}10`}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Heart size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Image Gallery */}
        <div style={styles.gallerySection}>
          <div 
            style={styles.mainImageContainer}
            onClick={() => !isMobile && setShowImageModal(true)}
          >
            <img 
              src={serviceImages[currentImageIndex]} 
              alt={service.name}
              style={styles.mainImage}
              onError={(e) => {
                e.target.src = `https://via.placeholder.com/800x400/0d8a72/ffffff?text=${encodeURIComponent(service.name)}`;
              }}
            />
            
            {serviceImages.length > 1 && (
              <>
                <button 
                  style={{ ...styles.imageNavButton, left: '16px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => 
                      prev === 0 ? serviceImages.length - 1 : prev - 1
                    );
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'white'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'}
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  style={{ ...styles.imageNavButton, right: '16px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => 
                      prev === serviceImages.length - 1 ? 0 : prev + 1
                    );
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'white'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'}
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>
          
          {serviceImages.length > 1 && (
            <div style={styles.thumbnailContainer}>
              {serviceImages.slice(0, 5).map((img, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.thumbnail,
                    ...(index === currentImageIndex ? styles.activeThumbnail : {})
                  }}
                  onClick={() => setCurrentImageIndex(index)}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.borderColor = primaryColor;
                      e.currentTarget.style.opacity = 1;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile && index !== currentImageIndex) {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.opacity = 0.7;
                    }
                  }}
                >
                  <img 
                    src={img} 
                    alt={`${service.name} - ${index + 1}`}
                    style={styles.thumbnailImage}
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/100x80/0d8a72/ffffff?text=Image`;
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Service Info & Booking Card */}
        <div style={styles.serviceInfo}>
          {/* Left Column - Service Details */}
          <div style={styles.leftColumn}>
            {/* Service Header */}
            <div style={styles.serviceHeader}>
              
              
              <h1 style={styles.serviceTitle}>{service.name}</h1>
              
              <p style={styles.serviceSubtitle}>
                {service.description || 'Professional service by verified experts with quality guarantee.'}
              </p>
              

                
            </div>

            {/* Features Grid */}
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: textDark }}>
                Why Choose This Service
              </h3>
              <div style={styles.featuresGrid}>
                {serviceFeatures.map((feature, index) => (
                  <div 
                    key={index} 
                    style={styles.featureCard}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <div style={styles.featureIcon}>
                      <feature.icon size={24} color={feature.color || primaryColor} />
                    </div>
                    <div style={styles.featureText}>{feature.text}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs Section */}
            <div style={styles.tabsContainer}>
              <div style={styles.tabsHeader}>
                {['overview', 'includes', 'faq', 'reviews'].map((tab) => (
                  <button
                    key={tab}
                    style={{
                      ...styles.tabButton,
                      ...(activeTab === tab ? styles.activeTab : {})
                    }}
                    onClick={() => setActiveTab(tab)}
                    onMouseEnter={(e) => {
                      if (!isMobile && activeTab !== tab) {
                        e.target.style.color = primaryColor;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile && activeTab !== tab) {
                        e.target.style.color = '#666';
                      }
                    }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div style={styles.tabContent}>
                {activeTab === 'overview' && (
                  <div>
                    <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '16px' }}>
                      {service.detailedDescription || 'Our professional service ensures top-quality work with trained experts. We provide reliable, efficient, and affordable solutions for all your home service needs.'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginTop: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={18} color={primaryColor} />
                        <span style={{ fontSize: '14px', color: textDark }}>
                          Duration: {service.duration || '2-3 hours'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Shield size={18} color={primaryColor} />
                        <span style={{ fontSize: '14px', color: textDark }}>
                          Warranty: {service.warranty || '30 days'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'includes' && (
                  <div style={styles.includesGrid}>
                    {serviceIncludes.map((item, index) => (
                      <div key={index} style={styles.includeItem}>
                        <CheckCircle size={16} style={styles.includeIcon} />
                        <span style={styles.includeText}>{item}</span>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'faq' && (
                  <div>
                    {[
                      { q: 'How long does the service take?', a: service.duration || 'Typically 2-3 hours depending on the service requirement.' },
                      { q: 'Are your professionals verified?', a: 'Yes, all our professionals are background verified, trained, and certified.' },
                      { q: 'What is your cancellation policy?', a: 'You can cancel free of charge up to 24 hours before the scheduled service.' },
                      { q: 'Do I need to provide any materials?', a: 'No, our professionals bring all necessary tools and materials.' }
                    ].map((faq, index) => (
                      <div key={index} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
                        <h4 style={{ fontWeight: '600', marginBottom: '8px', color: textDark }}>{faq.q}</h4>
                        <p style={{ color: '#666', lineHeight: '1.5' }}>{faq.a}</p>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <div style={{ backgroundColor: lightBg, padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div style={styles.ratingBadge}>
                          <Star size={16} fill="#FFD700" color="#FFD700" />
                          <span>4.5/5</span>
                        </div>
                        <span style={{ fontSize: '14px', color: '#666' }}>Based on 1,234 reviews</span>
                      </div>
                      
                      {[
                        { rating: 5, percent: 70 },
                        { rating: 4, percent: 20 },
                        { rating: 3, percent: 5 },
                        { rating: 2, percent: 3 },
                        { rating: 1, percent: 2 }
                      ].map((item) => (
                        <div key={item.rating} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '14px', width: '40px' }}>{item.rating}★</span>
                          <div style={{ flex: 1, height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${item.percent}%`, height: '100%', backgroundColor: '#FFD700' }}></div>
                          </div>
                          <span style={{ fontSize: '14px', color: '#666', width: '40px' }}>{item.percent}%</span>
                        </div>
                      ))}
                    </div>
                    
                    <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                      <p>Read detailed customer reviews after booking the service</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Benefits Section */}
            <div style={styles.benefitsSection}>
              <h3 style={styles.benefitsTitle}>
                <Sparkles size={20} color={primaryColor} />
                Benefits
              </h3>
              <div style={styles.benefitsGrid}>
                <div style={styles.benefitItem}>
                  <div style={styles.benefitIcon}>
                    <Award size={20} />
                  </div>
                  <span style={styles.benefitText}>Quality Service Guarantee</span>
                </div>
                <div style={styles.benefitItem}>
                  <div style={styles.benefitIcon}>
                    <ShieldCheck size={20} />
                  </div>
                  <span style={styles.benefitText}>Verified Professionals</span>
                </div>
                <div style={styles.benefitItem}>
                  <div style={styles.benefitIcon}>
                    <Clock size={20} />
                  </div>
                  <span style={styles.benefitText}>On-Time Service</span>
                </div>
                <div style={styles.benefitItem}>
                  <div style={styles.benefitIcon}>
                    <Tag size={20} />
                  </div>
                  <span style={styles.benefitText}>Best Price Guarantee</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div>
            <div style={styles.bookingCard}>
              {/* Price Display */}
              <div style={styles.priceSection}>
                <div style={styles.currentPrice}>₹{formatPrice(currentPrice * selectedQuantity)}</div>
                {service.pricingType !== 'inspection' && (
                  <div style={styles.priceNote}>
                    <Clock size={14} />
                    <span>Service Duration: {service.duration || '2-3 hours'}</span>
                  </div>
                )}
                {service.pricingType === 'inspection' && (
                  <div style={styles.priceNote}>
                    <AlertCircle size={14} />
                    <span>Final bill after inspection</span>
                  </div>
                )}
              </div>

              {/* BHK Selector */}
              {service.pricingType === 'bhk-based' && service.bhkPrices && (
                <div style={styles.bhkSelector}>
                  <div style={styles.bhkTitle}>Select Apartment Size</div>
                  <div style={styles.bhkOptions}>
                    {Object.entries(service.bhkPrices).map(([bhk, price]) => (
                      <button
                        key={bhk}
                        style={{
                          ...styles.bhkOption,
                          ...(selectedBHK === bhk ? styles.activeBhkOption : {})
                        }}
                        onClick={() => setSelectedBHK(bhk)}
                        onMouseEnter={(e) => {
                          if (!isMobile && selectedBHK !== bhk) {
                            e.currentTarget.style.borderColor = primaryColor;
                            e.currentTarget.style.backgroundColor = `${primaryColor}05`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isMobile && selectedBHK !== bhk) {
                            e.currentTarget.style.borderColor = `${primaryColor}20`;
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <span style={styles.bhkLabel}>{bhk}</span>
                        <span style={styles.bhkPrice}>₹{formatPrice(price)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Variant Selector */}
              {service.pricingType === 'variants' && service.variants && service.variants.length > 0 && (
                <div style={styles.variantSelector}>
                  <div style={styles.bhkTitle}>Select Service Option</div>
                  {service.variants.map((variant) => (
                    <button
                      key={variant.id}
                      style={{
                        ...styles.variantOption,
                        ...(selectedVariant?.id === variant.id ? styles.activeVariantOption : {})
                      }}
                      onClick={() => setSelectedVariant(variant)}
                      onMouseEnter={(e) => {
                        if (!isMobile && selectedVariant?.id !== variant.id) {
                          e.currentTarget.style.borderColor = primaryColor;
                          e.currentTarget.style.backgroundColor = `${primaryColor}05`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isMobile && selectedVariant?.id !== variant.id) {
                          e.currentTarget.style.borderColor = `${primaryColor}20`;
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <div style={styles.variantInfo}>
                        <span style={styles.variantName}>{variant.name}</span>
                        {variant.description && (
                          <span style={styles.variantDesc}>{variant.description}</span>
                        )}
                      </div>
                      <span style={{ fontSize: '16px', fontWeight: '700', color: primaryColor }}>
                        ₹{formatPrice(variant.price)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Quantity Selector */}
              {service.pricingType !== 'bhk-based' && service.pricingType !== 'inspection' && (
                <div style={styles.quantitySection}>
                  <div style={styles.quantityTitle}>Select Quantity</div>
                  <div style={styles.quantityControls}>
                    <button
                      style={styles.quantityButton}
                      onClick={() => handleQuantityChange(selectedQuantity - 1)}
                      disabled={selectedQuantity <= 1}
                      onMouseEnter={(e) => {
                        if (!isMobile && selectedQuantity > 1) {
                          e.target.style.backgroundColor = `${primaryColor}10`;
                          e.target.style.borderColor = primaryColor;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isMobile && selectedQuantity > 1) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = `${primaryColor}30`;
                        }
                      }}
                    >
                      -
                    </button>
                    <span style={styles.quantityDisplay}>{selectedQuantity}</span>
                    <button
                      style={styles.quantityButton}
                      onClick={() => handleQuantityChange(selectedQuantity + 1)}
                      disabled={selectedQuantity >= 10}
                      onMouseEnter={(e) => {
                        if (!isMobile && selectedQuantity < 10) {
                          e.target.style.backgroundColor = `${primaryColor}10`;
                          e.target.style.borderColor = primaryColor;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isMobile && selectedQuantity < 10) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = `${primaryColor}30`;
                        }
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={styles.actionButtons}>
                <button
                  style={styles.addToCartButton}
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  onMouseEnter={(e) => {
                    if (!isMobile && !addingToCart) {
                      e.target.style.backgroundColor = `${primaryColor}05`;
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile && !addingToCart) {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <ShoppingCart size={20} />
                  {addingToCart ? 'Adding...' : 'Add to Cart'}
                </button>
                
                <button
                  style={styles.bookNowButton}
                  onClick={handleBookNow}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = primaryLight;
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = primaryColor;
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <Calendar size={20} />
                  {user ? 'Book Now' : 'Login to Book'}
                </button>
              </div>

              {/* Assurance Note */}
              <div style={{ marginTop: '20px', padding: '16px', backgroundColor: lightBg, borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <ShieldCheck size={16} color={primaryColor} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: textDark }}>Service Assurance</span>
                </div>
                <p style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                  Verified professionals • Quality guarantee • On-time service • No hidden charges
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Similar Services */}
        {similarServices.length > 0 && (
          <div style={styles.similarServices}>
            <h2 style={styles.sectionTitle}>Similar Services You Might Like</h2>
            <div style={styles.servicesGrid}>
              {similarServices.slice(0, isMobile ? 2 : 3).map((similarService) => (
                <div
                  key={similarService.id}
                  style={styles.serviceCard}
                  onClick={() => navigate(`/service/${similarService.id}`)}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                    }
                  }}
                >
                  <img 
                    src={similarService.image || `https://via.placeholder.com/400x240/0d8a72/ffffff?text=${encodeURIComponent(similarService.name)}`}
                    alt={similarService.name}
                    style={styles.serviceCardImage}
                  />
                  <div style={styles.serviceCardContent}>
                    <h3 style={styles.serviceCardTitle}>{similarService.name}</h3>
                    <div style={styles.serviceCardPrice}>
                      ₹{formatPrice(similarService.calculatedPrice || similarService.basePrice || 0)}
                    </div>
                    <button
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: primaryColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/service/${similarService.id}`);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && !isMobile && (
        <div 
          style={styles.imageModalOverlay}
          onClick={() => setShowImageModal(false)}
        >
          <div style={styles.imageModal} onClick={(e) => e.stopPropagation()}>
            <button 
              style={styles.modalClose}
              onClick={() => setShowImageModal(false)}
            >
              <X size={24} />
            </button>
            
            <img 
              src={serviceImages[currentImageIndex]} 
              alt={service.name}
              style={styles.modalImage}
            />
            
            {serviceImages.length > 1 && (
              <>
                <button
                  style={{ ...styles.modalNav, left: '20px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => 
                      prev === 0 ? serviceImages.length - 1 : prev - 1
                    );
                  }}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  style={{ ...styles.modalNav, right: '20px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => 
                      prev === serviceImages.length - 1 ? 0 : prev + 1
                    );
                  }}
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .service-detail-page {
              padding-bottom: 80px;
            }
            
            .booking-card {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              top: auto;
              border-radius: 20px 20px 0 0;
              box-shadow: 0 -10px 30px rgba(0,0,0,0.15);
              padding: 20px;
              margin: 0;
              animation: slideUp 0.3s ease;
              z-index: 1000;
            }
            
            .mobile-booking-actions {
              display: flex;
              gap: 12px;
              padding: 16px;
              background: white;
              border-top: 1px solid #f0f0f0;
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              z-index: 1001;
            }
            
            .mobile-book-btn {
              flex: 1;
              background: ${primaryColor};
              color: white;
              border: none;
              padding: 16px;
              border-radius: 12px;
              font-size: 16px;
              font-weight: 600;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
            }
            
            .mobile-cart-btn {
              width: 60px;
              background: white;
              color: ${primaryColor};
              border: 2px solid ${primaryColor};
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            /* Hide desktop booking card on mobile */
            .booking-card {
              display: none;
            }
            
            .mobile-booking-card {
              display: block !important;
            }
          }
          
          @media (min-width: 769px) {
            .mobile-booking-actions {
              display: none !important;
            }
            
            .booking-card {
              display: block !important;
            }
          }
          
          /* Touch improvements for mobile */
          @media (max-width: 768px) {
            button, .bhk-option, .variant-option {
              min-height: 44px;
              touch-action: manipulation;
            }
            
            input, select {
              font-size: 16px;
            }
          }
          
          /* Scrollbar styling */
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          
          ::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: ${primaryColor};
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: ${primaryLight};
          }
          
          /* Selection color */
          ::selection {
            background-color: ${primaryColor}30;
            color: ${textDark};
          }
          
          /* Focus styles for accessibility */
          button:focus-visible,
          .bhk-option:focus-visible,
          .variant-option:focus-visible {
            outline: 2px solid ${primaryColor};
            outline-offset: 2px;
          }
          
          /* Loading animation for add to cart */
          @keyframes checkmark {
            0% { transform: scale(0); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          
          .added-to-cart {
            animation: checkmark 0.3s ease;
          }
        `}
      </style>

      {/* Mobile Booking Actions (Fixed at bottom) */}
      {isMobile && (
        <div className="mobile-booking-actions" style={{
          display: 'flex',
          gap: '12px',
          padding: '16px',
          background: 'white',
          borderTop: '1px solid #f0f0f0',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1001,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
        }}>
          <button
            style={{
              flex: 1,
              background: primaryColor,
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              cursor: 'pointer',
            }}
            onClick={handleBookNow}
          >
            <Calendar size={20} />
            {user ? 'Book Now' : 'Login to Book'}
          </button>
          
          <button
            style={{
              width: '60px',
              background: 'white',
              color: primaryColor,
              border: `2px solid ${primaryColor}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={handleAddToCart}
            disabled={addingToCart}
          >
            <ShoppingCart size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ServiceDetail;